import { lazy, Suspense, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ECOSYSTEM_LABELS } from '@repo/shared';
import type {
  DependencyNode,
  Ecosystem,
  ProjectHealth,
  ScorecardCheck,
  VersionAdvisory,
  SeverityLevel,
} from '@repo/shared';
import { ApiError } from '../lib/api';
import {
  usePackage,
  useVersionDetail,
  useDependencies,
  useProjectHealth,
} from '../features/packages/hooks';
import { SourceLink } from '../components/SourceLink';
import { ExportJsonButton } from '../components/ExportJsonButton';
// Lazy so the canvas/force-graph bundle only loads when the graph view is opened.
const DependencyGraph = lazy(() =>
  import('../features/packages/DependencyGraph').then((m) => ({ default: m.DependencyGraph })),
);
import { exportFilename } from '../lib/exportJson';
import styles from './PackagePage.module.css';
import pageStyles from './pages.module.css';

/** Package page — `/pkg/$system/$name` where `name` is already URI-encoded. */
export function PackagePage() {
  const { system, name: encodedName } = useParams({ from: '/pkg/$system/$name' });
  const search = useSearch({ from: '/pkg/$system/$name' });
  const navigate = useNavigate();

  const rawName = decodeURIComponent(encodedName);
  const ecosystem = system as Ecosystem;

  const pkg = usePackage(system, rawName);

  // Determine active version: search param `v`, else defaultVersion, else first.
  const activeVersion: string | undefined =
    (search as { v?: string }).v ?? pkg.data?.defaultVersion ?? pkg.data?.versions[0]?.version;

  const detail = useVersionDetail(system, rawName, activeVersion);

  function handleVersionChange(version: string) {
    void navigate({
      to: '/pkg/$system/$name',
      params: { system, name: encodedName },
      search: { v: version },
    });
  }

  // ---- Loading state ----
  if (pkg.isPending) {
    return (
      <div className={pageStyles.page}>
        <p className={pageStyles.muted}>Loading package…</p>
      </div>
    );
  }

  // ---- Error state ----
  if (pkg.isError) {
    const is404 = pkg.error instanceof ApiError && pkg.error.status === 404;
    return (
      <div className={pageStyles.page}>
        {is404 ? (
          <div className={styles.notFound}>
            <h2 className={styles.notFoundTitle}>Package not found</h2>
            <p className={pageStyles.muted}>
              <strong>{rawName}</strong> could not be found in{' '}
              <strong>{ECOSYSTEM_LABELS[ecosystem]}</strong>. Double-check the name and ecosystem.
            </p>
          </div>
        ) : (
          <p className={pageStyles.error}>Failed to load package: {pkg.error.message}</p>
        )}
      </div>
    );
  }

  const summary = pkg.data;
  const ecosystemLabel = ECOSYSTEM_LABELS[ecosystem] ?? ecosystem;

  return (
    <div className={pageStyles.page}>
      {/* Package header */}
      <div className={styles.header}>
        <div>
          <span className={styles.ecosystemTag}>{ecosystemLabel}</span>
          <h1 className={styles.packageName}>{rawName}</h1>
        </div>
      </div>

      {/* Version picker */}
      <div className={styles.versionRow}>
        <label htmlFor="version-picker" className={pageStyles.muted}>
          Version
        </label>
        <select
          id="version-picker"
          className={styles.versionSelect}
          value={activeVersion ?? ''}
          onChange={(e) => handleVersionChange(e.target.value)}
        >
          {summary.versions.map((v) => (
            <option key={v.version} value={v.version}>
              {v.version}
              {v.isDefault ? ' (latest)' : ''}
              {v.isDeprecated ? ' ⚠ deprecated' : ''}
            </option>
          ))}
        </select>
        {detail.data && activeVersion && (
          <ExportJsonButton
            className={styles.exportButton}
            filename={exportFilename(system, rawName, activeVersion)}
            kind="package-version"
            data={{ package: summary, version: detail.data }}
          />
        )}
      </div>

      {/* Version detail */}
      {detail.isPending && <p className={pageStyles.muted}>Loading version details…</p>}
      {detail.isError && (
        <p className={pageStyles.error}>Failed to load version: {detail.error.message}</p>
      )}
      {detail.data && (
        <VersionDetail
          key={activeVersion}
          data={detail.data}
          system={system}
          encodedName={encodedName}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Sub-components
// ------------------------------------------------------------------

import type { VersionDetail as VersionDetailType } from '@repo/shared';

function VersionDetail({
  data,
  system,
  encodedName,
}: {
  data: VersionDetailType;
  system: string;
  encodedName: string;
}) {
  const navigate = useNavigate();

  function navigateToVersion(version: string) {
    void navigate({
      to: '/pkg/$system/$name',
      params: { system, name: encodedName },
      search: { v: version },
    });
  }

  // First verified provenance entry (if any)
  const verifiedProvenance = data.provenance.find((p) => p.verified);

  return (
    <div className={styles.versionDetail}>
      {/* Deprecation banner */}
      {data.isDeprecated && (
        <div className={styles.deprecatedBanner} role="alert">
          <strong>Deprecated</strong>
          {data.deprecatedReason
            ? ` — ${data.deprecatedReason}`
            : ' — this version is deprecated. Consider upgrading.'}
        </div>
      )}

      {/* Recommended upgrade callout */}
      {data.recommendedVersion && (
        <div className={styles.upgradeCallout}>
          <strong>Recommended upgrade:</strong>{' '}
          <button
            className={styles.versionLink}
            onClick={() => navigateToVersion(data.recommendedVersion!)}
          >
            {data.recommendedVersion}
          </button>
        </div>
      )}

      {/* Advisories */}
      {data.advisories.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Advisories ({data.advisories.length})</h2>
          <ul className={styles.advisoryList}>
            {data.advisories.map((advisory) => (
              <AdvisoryCard key={advisory.id} advisory={advisory} />
            ))}
          </ul>
        </section>
      ) : (
        <section className={styles.section}>
          <p className={styles.noAdvisories}>No known advisories for this version.</p>
        </section>
      )}

      {/* Dependencies (on demand) */}
      <DependenciesSection
        system={data.versionKey.system}
        name={data.versionKey.name}
        version={data.versionKey.version}
      />

      {/* Licenses + links + metadata */}
      <div className={styles.metaRow}>
        {data.publishedAt && (
          <div className={styles.metaBlock}>
            <span className={pageStyles.muted}>Published: </span>
            {new Date(data.publishedAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
        )}
        {data.dependents && data.dependents.total > 0 && (
          <div className={styles.metaBlock}>
            <span className={pageStyles.muted}>Used by: </span>
            {data.dependents.total.toLocaleString()} packages (
            {data.dependents.direct.toLocaleString()} direct)
          </div>
        )}
        {verifiedProvenance && (
          <div className={styles.metaBlock}>
            {verifiedProvenance.url ? (
              <a
                href={verifiedProvenance.url}
                target="_blank"
                rel="noreferrer"
                className={styles.provenanceBadge}
              >
                ✓ Verified provenance
                {verifiedProvenance.sourceRepository && (
                  <span className={styles.provenanceRepo}>
                    {' '}
                    — {verifiedProvenance.sourceRepository}
                  </span>
                )}
              </a>
            ) : (
              <span className={styles.provenanceBadge}>
                ✓ Verified provenance
                {verifiedProvenance.sourceRepository && (
                  <span className={styles.provenanceRepo}>
                    {' '}
                    — {verifiedProvenance.sourceRepository}
                  </span>
                )}
              </span>
            )}
          </div>
        )}
        {data.licenses.length > 0 && (
          <div className={styles.metaBlock}>
            <span className={pageStyles.muted}>Licenses: </span>
            {data.licenses.join(', ')}
          </div>
        )}
        {data.links.length > 0 && (
          <div className={styles.linksList}>
            {data.links.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className={styles.externalLink}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Project health card (lazy — only when relatedProjectId is known) */}
      {data.relatedProjectId && <ProjectHealthCard projectId={data.relatedProjectId} />}
    </div>
  );
}

function DependenciesSection({
  system,
  name,
  version,
}: {
  system: string;
  name: string;
  version: string;
}) {
  const navigate = useNavigate();
  const [requested, setRequested] = useState(false);
  const [view, setView] = useState<'list' | 'graph'>('list');
  const deps = useDependencies(system, name, version, requested);

  function navigateToPackage(depSystem: string, depName: string, depVersion: string) {
    void navigate({
      to: '/pkg/$system/$name',
      params: { system: depSystem, name: encodeURIComponent(depName) },
      search: { v: depVersion },
    });
  }

  return (
    <section className={styles.section}>
      <div className={styles.versionRow}>
        <h2 className={styles.sectionTitle}>Dependencies</h2>
        {!requested && (
          <button type="button" className={styles.depsButton} onClick={() => setRequested(true)}>
            Fetch dependencies
          </button>
        )}
      </div>

      {requested && deps.isPending && (
        <p className={pageStyles.muted}>Resolving dependency graph…</p>
      )}
      {requested && deps.isError && (
        <p className={pageStyles.error}>Failed to load dependencies: {deps.error.message}</p>
      )}
      {deps.data && deps.data.dependencies.length === 0 && (
        <p className={pageStyles.muted}>
          No resolved dependencies available for this version.
          {system !== 'NPM' && system !== 'PYPI' && system !== 'CARGO' && system !== 'MAVEN'
            ? ' deps.dev only resolves dependency graphs for npm, PyPI, Cargo, and Maven.'
            : ' It may have no dependencies, or deps.dev may not have resolved a recently published version yet.'}
        </p>
      )}
      {deps.data && deps.data.dependencies.length > 0 && (
        <>
          <div className={styles.depsCounts}>
            <span className={styles.depsCountChip}>{deps.data.counts.direct} direct</span>
            <span className={styles.depsCountChip}>{deps.data.counts.indirect} transitive</span>
            <span className={styles.depsCountChip}>{deps.data.counts.total} total</span>
            <div className={styles.viewToggle}>
              <button
                type="button"
                className={styles.viewToggleButton}
                data-active={view === 'list'}
                onClick={() => setView('list')}
              >
                List
              </button>
              <button
                type="button"
                className={styles.viewToggleButton}
                data-active={view === 'graph'}
                onClick={() => setView('graph')}
              >
                Graph
              </button>
            </div>
          </div>

          {view === 'graph' && (
            <Suspense fallback={<p className={pageStyles.muted}>Loading graph…</p>}>
              <DependencyGraph report={deps.data} onNavigate={navigateToPackage} />
            </Suspense>
          )}

          {view === 'list' &&
            (['DIRECT', 'INDIRECT'] as const).map((relation) => {
              const group = deps.data.dependencies.filter((d) => d.relation === relation);
              if (group.length === 0) return null;
              return (
                <div key={relation} className={styles.depGroup}>
                  <h3 className={styles.depGroupTitle}>
                    {relation === 'DIRECT' ? 'Direct' : 'Transitive'} ({group.length})
                  </h3>
                  <ul className={styles.depList}>
                    {group.map((dep) => (
                      <DependencyRow
                        key={`${dep.system}:${dep.name}:${dep.version}`}
                        dep={dep}
                        onNavigate={navigateToPackage}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
        </>
      )}
    </section>
  );
}

function ProjectHealthCard({ projectId }: { projectId: string }) {
  const { data, isError, isPending } = useProjectHealth(projectId);

  // Silent on loading — avoid layout shift for the common case where it loads quickly.
  // On 404 or other errors, render nothing.
  if (isPending || isError || !data) return null;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Project Health</h2>
      <div className={styles.projectCard}>
        {data.description && <p className={styles.projectDescription}>{data.description}</p>}

        {/* Stats row */}
        <div className={styles.projectStats}>
          {data.starsCount != null && (
            <span className={styles.projectStat}>
              <span className={styles.projectStatLabel}>Stars</span>
              {data.starsCount.toLocaleString()}
            </span>
          )}
          {data.forksCount != null && (
            <span className={styles.projectStat}>
              <span className={styles.projectStatLabel}>Forks</span>
              {data.forksCount.toLocaleString()}
            </span>
          )}
          {data.openIssuesCount != null && (
            <span className={styles.projectStat}>
              <span className={styles.projectStatLabel}>Open Issues</span>
              {data.openIssuesCount.toLocaleString()}
            </span>
          )}
          {data.license && (
            <span className={styles.projectStat}>
              <span className={styles.projectStatLabel}>License</span>
              {data.license}
            </span>
          )}
        </div>

        {/* Homepage link */}
        {data.homepage && (
          <div className={styles.projectHomepage}>
            <a
              href={data.homepage}
              target="_blank"
              rel="noreferrer"
              className={styles.externalLink}
            >
              {data.homepage}
            </a>
          </div>
        )}

        {/* OpenSSF Scorecard */}
        {data.scorecard && <ScorecardSection scorecard={data.scorecard} />}
      </div>
    </section>
  );
}

function ScorecardSection({ scorecard }: { scorecard: NonNullable<ProjectHealth['scorecard']> }) {
  return (
    <div className={styles.scorecard}>
      <div className={styles.scorecardHeader}>
        <a
          href="https://securityscorecards.dev"
          target="_blank"
          rel="noreferrer"
          className={styles.scorecardTitle}
        >
          OpenSSF Scorecard
        </a>
        {scorecard.overallScore != null && (
          <span className={styles.scorecardOverall}>{scorecard.overallScore.toFixed(1)} / 10</span>
        )}
        {scorecard.date && (
          <span className={`${pageStyles.muted} ${styles.scorecardDate}`}>
            {new Date(scorecard.date).toLocaleDateString()}
          </span>
        )}
      </div>
      {scorecard.checks.length > 0 && (
        <div className={styles.scorecardChecks}>
          {scorecard.checks.map((check) => (
            <ScorecardCheckChip key={check.name} check={check} />
          ))}
        </div>
      )}
    </div>
  );
}

function ScorecardCheckChip({ check }: { check: ScorecardCheck }) {
  const isNa = check.score === -1;
  const chipClass = isNa
    ? styles.scorecardCheckNa
    : check.score >= 7
      ? styles.scorecardCheckGood
      : check.score >= 4
        ? styles.scorecardCheckMedium
        : styles.scorecardCheckPoor;

  const chip = (
    <span className={`${styles.scorecardCheck} ${chipClass}`} title={check.reason ?? undefined}>
      {check.name}
      <span className={styles.scorecardCheckScore}>{isNa ? 'n/a' : check.score}</span>
    </span>
  );

  if (check.documentationUrl) {
    return (
      <a
        href={check.documentationUrl}
        target="_blank"
        rel="noreferrer"
        className={styles.scorecardCheckLink}
      >
        {chip}
      </a>
    );
  }

  return chip;
}

function DependencyRow({
  dep,
  onNavigate,
}: {
  dep: DependencyNode;
  onNavigate: (system: string, name: string, version: string) => void;
}) {
  return (
    <li className={styles.depItem}>
      <button
        type="button"
        className={styles.depLink}
        onClick={() => onNavigate(dep.system, dep.name, dep.version)}
      >
        {dep.name}
      </button>
      <span className={styles.depVersion}>{dep.version}</span>
      {dep.requirement && <span className={styles.depRequirement}>({dep.requirement})</span>}
      {dep.bundled && <span className={styles.depBundled}>bundled</span>}
    </li>
  );
}

const SEVERITY_LABELS: Record<SeverityLevel, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  UNKNOWN: 'Unknown',
};

const SEVERITY_CLASS: Record<SeverityLevel, string> = {
  CRITICAL: styles.severityCritical ?? '',
  HIGH: styles.severityHigh ?? '',
  MEDIUM: styles.severityMedium ?? '',
  LOW: styles.severityLow ?? '',
  UNKNOWN: styles.severityUnknown ?? '',
};

function AdvisoryCard({ advisory }: { advisory: VersionAdvisory }) {
  const hasFixedVersions = advisory.fixedVersions.length > 0;

  return (
    <li className={styles.advisoryCard}>
      <div className={styles.advisoryHeader}>
        <span
          className={`${styles.severityBadge} ${SEVERITY_CLASS[advisory.severity]}`}
          data-severity={advisory.severity}
        >
          {SEVERITY_LABELS[advisory.severity]}
        </span>
        <span className={styles.advisoryId}>{advisory.id}</span>
        {advisory.cvssScore != null && (
          <span className={pageStyles.muted}>CVSS {advisory.cvssScore.toFixed(1)}</span>
        )}
      </div>

      {advisory.aliases.length > 0 && (
        <div className={styles.aliases}>
          {advisory.aliases.map((alias) => (
            <span key={alias} className={styles.alias}>
              {alias}
            </span>
          ))}
        </div>
      )}

      {advisory.summary && <p className={styles.advisorySummary}>{advisory.summary}</p>}

      <div className={styles.fixedIn}>
        {hasFixedVersions ? (
          <>
            <span className={pageStyles.muted}>Fixed in: </span>
            {advisory.fixedVersions.map((v) => (
              <span key={v} className={styles.fixedBadge}>
                {v}
              </span>
            ))}
          </>
        ) : (
          <span className={styles.noFix}>No fix available</span>
        )}
      </div>

      {advisory.url && <SourceLink label="OSV.dev" href={advisory.url} />}
    </li>
  );
}
